import React, { useState, useEffect, useMemo } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
} from "chart.js";

import apiClient from "../../axiosInstance";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
);
const Dashboard = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
  const userRole = String(authUser?.user?.role || "")
    .trim()
    .toLowerCase();
  const isCashier = userRole === "cashier";

  const [month, setMonth] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());

  const [summary, setSummary] = useState({
    registeredVehicles: 0,
    expenses: 0,
    paymentsReceived: 0,
    outstandingPayments: 0,
  });

  const [purposeChart, setPurposeChart] = useState({
    labels: [],
    data: [],
  });
  const [branchChart, setBranchChart] = useState({
    labels: [],
    data: [],
  });
  const [paymentTrend, setPaymentTrend] = useState({
    labels: [],
    data: [],
  });

  const [inspectionResults, setInspectionResults] = useState({
    labels: [],
    data: [],
  });
  const [paymentMethods, setPaymentMethods] = useState({
    labels: [],
    data: [],
  });

  const [cashierSummary, setCashierSummary] = useState({
    collectedAmount: 0,
    remainingOutstanding: 0,
    todayCollected: 0,
    receiptsCount: 0,
    remainingCount: 0,
  });

  const [cashierPaymentMethods, setCashierPaymentMethods] = useState({
    labels: [],
    data: [],
  });

  const [cashierMonthlyTrend, setCashierMonthlyTrend] = useState({
    labels: [],
    data: [],
  });

  const [cashierCollectionsByPurpose, setCashierCollectionsByPurpose] = useState({
    labels: [],
    data: [],
  });

  const [cashierOutstandingByPurpose, setCashierOutstandingByPurpose] = useState({
    labels: [],
    data: [],
  });

  const cashierTrendTarget = useMemo(() => {
    const source = Array.isArray(cashierMonthlyTrend.data)
      ? cashierMonthlyTrend.data
      : [];
    return source.map((value) => Math.round(Number(value || 0) * 1.08));
  }, [cashierMonthlyTrend]);

  const months = [
    { value: "", label: "All Months" },
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Fetch dashboard data
  const fetchSummary = async () => {
    try {
      const res = await apiClient.get("api/dashboard/summary", {
        params: { year, month },
      });
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to load dashboard summary", err);
    }
  };
  const fetchPurpose = async () => {
    try {
      const res = await apiClient.get("/api/dashboard/vehiclePurpose", {
        params: { year, month },
      });
      setPurposeChart(res.data);
    } catch (err) {
      console.error("Failed to load vehicle purpose data", err);
    }
  };
  const fetchBranchRegistrations = async () => {
    try {
      const res = await apiClient.get("/api/dashboard/branchRegistrations", {
        params: { year, month },
      });
      setBranchChart(res.data);
    } catch (err) {
      console.error("Failed to load branch registrations", err);
    }
  };
  const fetchPaymentTrend = async () => {
    try {
      const res = await apiClient.get("/api/dashboard/paymentTrend", {
        params: { year },
      });
      setPaymentTrend(res.data);
    } catch (err) {
      console.error("Failed to load payment trend", err);
    }
  };
  const fetchInspectionResults = async () => {
    try {
      const res = await apiClient.get("api/dashboard/inspectionResults", {
        params: { year, month },
      });
      setInspectionResults(res.data);
    } catch (err) {
      console.error("Failed to load inspection results", err);
    }
  };
  const fetchPaymentMethods = async () => {
    try {
      const res = await apiClient.get("api/dashboard/paymentMethods", {
        params: { year, month },
      });
      setPaymentMethods(res.data);
    } catch (err) {
      console.error("Failed to load payment methods", err);
    }
  };

  const fetchCashierSummary = async () => {
    try {
      const res = await apiClient.get("/api/dashboard/cashierSummary", {
        params: { year, month },
      });
      setCashierSummary(res.data || {});
    } catch (err) {
      console.error("Failed to load cashier summary", err);
    }
  };

  const fetchCashierPaymentMethods = async () => {
    try {
      const res = await apiClient.get("/api/dashboard/cashierPaymentMethods", {
        params: { year, month },
      });
      setCashierPaymentMethods(res.data || { labels: [], data: [] });
    } catch (err) {
      console.error("Failed to load cashier payment methods", err);
    }
  };

  const fetchCashierMonthlyTrend = async () => {
    try {
      const res = await apiClient.get("/api/dashboard/cashierMonthlyTrend", {
        params: { year },
      });
      setCashierMonthlyTrend(res.data || { labels: [], data: [] });
    } catch (err) {
      console.error("Failed to load cashier monthly trend", err);
    }
  };

  const fetchCashierCollectionsByPurpose = async () => {
    try {
      const res = await apiClient.get("/api/dashboard/cashierCollectionsByPurpose", {
        params: { year, month },
      });
      setCashierCollectionsByPurpose(res.data || { labels: [], data: [] });
    } catch (err) {
      console.error("Failed to load cashier collections by purpose", err);
    }
  };

  const fetchCashierOutstandingByPurpose = async () => {
    try {
      const res = await apiClient.get("/api/dashboard/cashierOutstandingByPurpose", {
        params: { year, month },
      });
      setCashierOutstandingByPurpose(res.data || { labels: [], data: [] });
    } catch (err) {
      console.error("Failed to load cashier outstanding by purpose", err);
    }
  };

  // Fetch whenever month/year changes
  useEffect(() => {
    if (isCashier) {
      fetchCashierSummary();
      fetchCashierPaymentMethods();
      fetchCashierMonthlyTrend();
      fetchCashierCollectionsByPurpose();
      fetchCashierOutstandingByPurpose();
      return;
    }

    fetchSummary();
    fetchPurpose();
    fetchBranchRegistrations();
    fetchPaymentTrend();
    fetchInspectionResults();
    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, isCashier]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Form className="mb-4">
            <Row className="flex align-items-end">
              <Col md={9}>
                <h3>{isCashier ? "Cashier Dashboard" : "Dashboard"}</h3>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label>Month</Label>
                  <Input
                    type="select"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={1}>
                <FormGroup>
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>

          {isCashier ? (
            <>
              {(() => {
                const collected = Number(cashierSummary.collectedAmount || 0);
                const outstanding = Number(cashierSummary.remainingOutstanding || 0);
                const receipts = Number(cashierSummary.receiptsCount || 0);
                const avgReceipt = receipts > 0 ? collected / receipts : 0;
                const collectionRate =
                  collected + outstanding > 0
                    ? (collected / (collected + outstanding)) * 100
                    : 0;

                return (
                  <>
              <Row className="justify-content-end">
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-13 text-muted text-uppercase mb-2">
                            My Collections
                          </p>
                          <h3 className="mb-0 fw-bold">
                            ${Number(cashierSummary.collectedAmount || 0).toLocaleString()}
                          </h3>
                        </div>
                        <div className="text-success">
                          <i className="mdi mdi-cash font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-13 text-muted text-uppercase mb-2">
                            Remaining in Branch
                          </p>
                          <h3 className="mb-0 fw-bold text-danger">
                            ${Number(cashierSummary.remainingOutstanding || 0).toLocaleString()}
                          </h3>
                        </div>
                        <div className="text-danger">
                          <i className="mdi mdi-alert-circle font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-13 text-muted text-uppercase mb-2">
                            Today Collected
                          </p>
                          <h3 className="mb-0 fw-bold text-primary">
                            ${Number(cashierSummary.todayCollected || 0).toLocaleString()}
                          </h3>
                        </div>
                        <div className="text-primary">
                          <i className="mdi mdi-calendar-today font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-13 text-muted text-uppercase mb-2">
                            Receipts Issued
                          </p>
                          <h3 className="mb-0 fw-bold">
                            {receipts.toLocaleString()}
                          </h3>
                        </div>
                        <div className="text-info">
                          <i className="mdi mdi-receipt font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row className="mt-1">
                <Col md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-13 text-muted text-uppercase mb-2">
                            Average Receipt Value
                          </p>
                          <h3 className="mb-0 fw-bold">${avgReceipt.toLocaleString()}</h3>
                        </div>
                        <div className="text-warning">
                          <i className="mdi mdi-calculator-variant font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <p className="text-truncate font-size-13 text-muted text-uppercase mb-2">
                            Cashier Collection Rate
                          </p>
                          <h3 className="mb-2 fw-bold text-success">
                            {collectionRate.toFixed(1)}%
                          </h3>
                          <small className="text-muted">
                            Based on my collected amount vs outstanding
                          </small>
                        </div>
                        <div className="text-success">
                          <i className="mdi mdi-chart-line font-size-24"></i>
                        </div>
                      </div>
                      <div className="progress mt-3" style={{ height: "8px" }}>
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{ width: `${Math.min(collectionRate, 100)}%` }}
                          aria-valuenow={collectionRate}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <h6 className="mb-3">My Payment Methods Breakdown</h6>
                      <div style={{ height: "260px" }}>
                        <Pie
                          data={{
                            labels: cashierPaymentMethods.labels,
                            datasets: [
                              {
                                data: cashierPaymentMethods.data,
                                backgroundColor: [
                                  "#2196F3",
                                  "#FF9800",
                                  "#9C27B0",
                                  "#4CAF50",
                                  "#E91E63",
                                ],
                              },
                            ],
                          }}
                          options={{
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { position: "bottom", labels: { usePointStyle: true } },
                            },
                          }}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <h6 className="mb-3">Cashier Snapshot</h6>
                      <div className="mt-3">
                        <p className="mb-2">
                          <strong>Unpaid charges count:</strong>{" "}
                          {Number(cashierSummary.remainingCount || 0).toLocaleString()}
                        </p>
                        <p className="mb-2">
                          <strong>Collection vs Remaining:</strong>{" "}
                          ${collected.toLocaleString()} collected / ${" "}
                          {outstanding.toLocaleString()} remaining
                        </p>
                        <p className="mb-0 text-muted">
                          Tip: prioritize unpaid charges to reduce branch backlog.
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row className="mt-3 g-3">
                <Col xl={8} md={8}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">My Monthly Collection Trend ({year})</h6>
                        <span className="badge rounded-pill bg-soft-success text-success">
                          Forecast View
                        </span>
                      </div>
                      <div style={{ height: "360px" }}>
                        <Line
                          data={{
                            labels: cashierMonthlyTrend.labels,
                            datasets: [
                              {
                                label: "Actual",
                                data: cashierMonthlyTrend.data,
                                borderColor: "#22c55e",
                                backgroundColor: (context) => {
                                  const { ctx, chartArea } = context.chart;
                                  if (!chartArea) {
                                    return "rgba(34,197,94,0.18)";
                                  }
                                  const gradient = ctx.createLinearGradient(
                                    0,
                                    chartArea.top,
                                    0,
                                    chartArea.bottom,
                                  );
                                  gradient.addColorStop(0, "rgba(34,197,94,0.35)");
                                  gradient.addColorStop(1, "rgba(34,197,94,0.03)");
                                  return gradient;
                                },
                                fill: true,
                                tension: 0.45,
                                pointRadius: 3,
                                pointHoverRadius: 6,
                                pointBorderWidth: 2,
                                pointBackgroundColor: "#fff",
                                borderWidth: 3,
                              },
                              {
                                label: "Target",
                                data: cashierTrendTarget,
                                borderColor: "#3b82f6",
                                fill: false,
                                borderDash: [6, 6],
                                tension: 0.45,
                                pointRadius: 0,
                                borderWidth: 2,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            interaction: {
                              mode: "index",
                              intersect: false,
                            },
                            plugins: {
                              legend: {
                                position: "top",
                                labels: { usePointStyle: true, boxWidth: 8 },
                              },
                              tooltip: {
                                callbacks: {
                                  label: (context) =>
                                    `${context.dataset.label}: $${Number(
                                      context.parsed.y || 0,
                                    ).toLocaleString()}`,
                                },
                              },
                            },
                            scales: {
                              x: {
                                grid: { display: false },
                              },
                              y: {
                                beginAtZero: true,
                                grid: { color: "#f1f3f8" },
                                ticks: {
                                  callback: (value) => `$${Number(value).toLocaleString()}`,
                                },
                              },
                            },
                          }}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={4} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Outstanding by Service</h6>
                        <span className="badge rounded-pill bg-soft-danger text-danger">
                          Live
                        </span>
                      </div>
                      <div style={{ height: "320px" }}>
                        <Pie
                          data={{
                            labels: cashierOutstandingByPurpose.labels,
                            datasets: [
                              {
                                data: cashierOutstandingByPurpose.data,
                                backgroundColor: [
                                  "#ef4444",
                                  "#f59e0b",
                                  "#8b5cf6",
                                  "#0ea5e9",
                                  "#14b8a6",
                                  "#78716c",
                                ],
                                borderWidth: 2,
                                borderColor: "#fff",
                              },
                            ],
                          }}
                          options={{
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: "bottom",
                                labels: { usePointStyle: true, boxWidth: 8 },
                              },
                            },
                          }}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>

              </Row>
                  </>
                );
              })()}
            </>
          ) : (
            <>

          <Row className="justify-content-end">
            <Col md={8}>
              <Row>
                {/* Registered Vehicles */}
                <Col md={3}>
                  <Card>
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-14 mb-2">
                            Registered Vehicles
                          </p>
                          <h4 className="mb-0">{summary.registeredVehicles}</h4>
                        </div>
                        <div className="text-primary">
                          <i className="mdi mdi-car font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                {/* Payments Received */}
                <Col md={3}>
                  <Card>
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-14 mb-2">
                            Payments Received
                          </p>
                          <h4 className="mb-0">
                            ${summary.paymentsReceived.toLocaleString()}
                          </h4>
                        </div>
                        <div className="text-success">
                          <i className="mdi mdi-cash font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                {/* Expenses */}
                <Col md={3}>
                  <Card>
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-14 mb-2">
                            Expenses
                          </p>
                          <h4 className="mb-0">
                            ${summary.expenses.toLocaleString()}
                          </h4>
                        </div>
                        <div className="text-primary">
                          <i className="mdi mdi-cash-minus font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-1 overflow-hidden">
                          <p className="text-truncate font-size-14 mb-2">
                            Balance
                          </p>
                          <h4 className="mb-0">
                            ${(summary.paymentsReceived - summary.expenses).toLocaleString()}
                          </h4>
                        </div>
                        <div className="text-info">
                          <i className="mdi mdi-bank font-size-24"></i>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>

            {/* Outstanding Payments */}
            <Col md={4}>
              <Card>
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-1 overflow-hidden">
                      <p className="text-truncate font-size-14 mb-2">
                        Outstanding Payments
                      </p>
                      <h4 className="mb-0">
                        ${summary.outstandingPayments.toLocaleString()}
                      </h4>
                    </div>
                    <div className="text-danger">
                      <i className="mdi mdi-alert-circle font-size-24"></i>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col md={8}>
              <Card>
                <CardBody>
                  <h5>Monthly Payment Trend ({year})</h5>
                  <div style={{ height: "500px" }}>
                    <Line
                      data={{
                        labels: paymentTrend.labels,
                        datasets: [
                          {
                            label: "Payments Received",
                            data: paymentTrend.data,
                            borderColor: "#4CAF50",
                            backgroundColor: "rgba(76, 175, 80, 0.2)",
                            fill: true,
                            tension: 0.3, // smooth curves
                            pointBackgroundColor: "#388E3C",
                            pointRadius: 5,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: { position: "top" },
                          title: { display: false },
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                          },
                          y: {
                            beginAtZero: true,
                            grid: { color: "#eee" },
                            ticks: {
                              precision: 0,
                              callback: (value) => `$${value.toLocaleString()}`, // format y labels
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Row>
                <Col md={12}>
                  <Card>
                    <CardBody>
                      <h6>Inspection Results</h6>
                      <div style={{ height: "200px" }}>
                        <Pie
                          data={{
                            labels: inspectionResults.labels,
                            datasets: [
                              {
                                data: inspectionResults.data,
                                backgroundColor: ["#4CAF50", "#F44336"], // green = passed, red = failed
                              },
                            ],
                          }}
                          options={{
                            maintainAspectRatio: false,
                            plugins: { legend: { position: "bottom" } },
                          }}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={12} className="mt-3">
                  <Card>
                    <CardBody>
                      <h6>Payment Methods</h6>
                      <div style={{ height: "200px" }}>
                        <Pie
                          data={{
                            labels: paymentMethods.labels,
                            datasets: [
                              {
                                data: paymentMethods.data,
                                backgroundColor: [
                                  "#2196F3",
                                  "#FF9800",
                                  "#9C27B0",
                                  "#4CAF50",
                                  "#E91E63",
                                ],
                              },
                            ],
                          }}
                          options={{
                            maintainAspectRatio: false,
                            plugins: { legend: { position: "bottom" } },
                          }}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
          {/* Vehicle Purpose Chart */}
          <Row className="mt-4">
            <Col md={6}>
              <Card>
                <CardBody>
                  <h5>Vehicle Registrations by Purpose</h5>
                  <Bar
                    className="mt-4 pt-2"
                    data={{
                      labels: purposeChart.labels,
                      datasets: [
                        {
                          label: "Registrations",
                          data: purposeChart.data,
                          backgroundColor: "#2196F3", // blue bars
                          borderRadius: 6,
                          barThickness: 40,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { display: false },
                        title: { display: false },
                      },
                      scales: {
                        x: {
                          grid: { display: false }, // remove X grid lines
                        },
                        y: {
                          beginAtZero: true,
                          grid: { display: false }, // remove Y grid lines
                          ticks: {
                            precision: 0, // whole numbers only
                          },
                        },
                      },
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <CardBody>
                  <h5>Branch Registrations</h5>
                  <Bar
                    className="mt-4 pt-2"
                    data={{
                      labels: branchChart.labels,
                      datasets: [
                        {
                          label: "Registrations",
                          data: branchChart.data,
                          backgroundColor: "#C02130",
                          borderRadius: 6,
                          barThickness: 40,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { display: false },
                      },
                      scales: {
                        x: {
                          grid: { display: false },
                        },
                        y: {
                          beginAtZero: true,
                          grid: { display: false },
                          ticks: { precision: 0 },
                        },
                      },
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
